import React, { useState } from 'react';
import { theme } from '../../config/theme';
import { motion } from 'framer-motion';

/**
 * Reusable Ad Banner component.
 * Displays a clickable advertisement with optional image, title, and description.
 * Matches the reference design with a red dashed border.
 *
 * Props:
 * - imageUrl: URL of the ad image
 * - linkUrl: Click-through URL (opens in new tab)
 * - altText: Alt text for the ad image
 * - title: Optional headline text overlay
 * - description: Optional description below the ad
 * - size: 'full' | 'medium' | 'small' (default: 'full')
 * - onAdClick: Optional callback when ad is clicked
 */
const AdBanner = ({
  imageUrl,
  linkUrl = '#',
  altText = 'Advertisement',
  title,
  description,
  size = 'full',
  onAdClick,
  style: externalStyle,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const sizeMap = {
    full: { maxWidth: '100%', minHeight: '180px' },
    medium: { maxWidth: '728px', minHeight: '140px' },
    small: { maxWidth: '400px', minHeight: '100px' },
  };

  const handleClick = () => {
    if (onAdClick) onAdClick();
    if (linkUrl && linkUrl !== '#') {
      window.open(linkUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
      style={{
        border: '2px dashed #E53935',
        borderRadius: theme.borderRadius.xl,
        overflow: 'hidden',
        cursor: linkUrl && linkUrl !== '#' ? 'pointer' : 'default',
        position: 'relative',
        ...sizeMap[size],
        backgroundColor: theme.colors.white,
        boxShadow: isHovered
          ? '0 8px 30px rgba(229, 57, 53, 0.15)'
          : '0 2px 10px rgba(0,0,0,0.04)',
        transition: 'box-shadow 0.3s ease, transform 0.3s ease',
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        ...externalStyle,
      }}
    >
      {/* "Ad" label */}
      <div style={{
        position: 'absolute',
        top: '8px',
        right: '10px',
        fontSize: '10px',
        fontWeight: theme.typography.fontWeight.bold,
        color: '#E53935',
        backgroundColor: 'rgba(229, 57, 53, 0.08)',
        padding: '2px 8px',
        borderRadius: theme.borderRadius.full,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        zIndex: 2,
      }}>
        Ad
      </div>

      {imageUrl ? (
        <img
          src={imageUrl}
          alt={altText}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      ) : (
        <div style={{
          padding: theme.spacing.xl,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: theme.spacing.sm,
          flex: 1,
        }}>
          {title && (
            <div style={{
              fontSize: theme.typography.fontSize.lg,
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.colors.text.primary,
            }}>
              {title}
            </div>
          )}
          {description && (
            <div style={{
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.text.secondary,
            }}>
              {description}
            </div>
          )}
          {!title && !description && (
            <div style={{
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.text.secondary,
              fontStyle: 'italic',
            }}>
              Sponsored content
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default AdBanner;
